// ─── Playful client-side gamification (v3.0) ───────────────────────────────
// All localStorage, no Supabase. Streak = consecutive days opening the app.
// Saves = cumulative bookmark taps. Night Owl = opened after 8pm once.
import React, { useEffect, useState } from "react";

const FUN_KEY = "gd-fun-v1";

function dayStr(d) {
  return d.getFullYear() + "-" + (d.getMonth() + 1) + "-" + d.getDate();
}

function loadFun() {
  try {
    const s = JSON.parse(localStorage.getItem(FUN_KEY) || "{}");
    return {
      saves: s.saves || 0,
      nightOwl: !!s.nightOwl,
      last: s.last || null,
      count: s.count || 0,
    };
  } catch {
    return { saves: 0, nightOwl: false, last: null, count: 0 };
  }
}

function saveFun(s) {
  try {
    localStorage.setItem(FUN_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

// Call once per app open: maintains the streak + night-owl flag.
export function pokeFun() {
  const s = loadFun();
  const today = dayStr(new Date());
  let { count, last } = s;
  if (last !== today) {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    count = last === dayStr(y) ? count + 1 : 1;
    last = today;
  }
  const nightOwl = s.nightOwl || new Date().getHours() >= 20;
  const next = { saves: s.saves || 0, nightOwl, last, count };
  saveFun(next);
  return { streak: count, nightOwl, saves: next.saves };
}

// A bookmark tap that ADDS (not removes) — the lifetime save counter.
export function bumpSave() {
  const s = loadFun();
  const next = { ...s, saves: (s.saves || 0) + 1 };
  saveFun(next);
  return next.saves;
}

// "You've saved $X this week": sum of (regularPrice − price) across the
// user's saved list items that have a listed was-price. Honest by
// construction — items without a was-price contribute $0.
export function savingsScore(savedItems, weeklyDeals) {
  const set = new Set((savedItems || []).map((i) => (i.text || "").toLowerCase()));
  let total = 0;
  let counted = 0;
  for (const d of weeklyDeals) {
    if (!set.has((d.item || "").toLowerCase())) continue;
    if (d.regularPrice != null && d.price != null && d.regularPrice > d.price) {
      total += d.regularPrice - d.price;
      counted += 1;
    }
  }
  return { total, counted };
}

// Playful count-up for the savings score — eases out, tabular numerals.
export function CountUp({ value, format }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setShown(value * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className="tnum">{format(shown)}</span>;
}
